import equal from 'fast-deep-equal/react';
import { Processing } from '@wg/wows-react-uikit';
import classnames from 'classnames';
import { memo, useCallback } from 'react';
import styles from './App.scss';

import type { RootState } from '~/store';

import RefManager, { RefManagerKeys } from '~/refManager';
import { isBluredSelector } from '~/selectors';
import { AppInit, getData as getDataAction } from '~/store/app';
import { loadProducts } from '~/store/wowsCommerce';
import { initWowsCommerce } from '~/wowsCommerce';
import { blueprintConfig } from '~/configs/blueprintConfig';
import { IS_DEV_MODE, IS_SUPER_TEST } from '~/constants';
import {
    Footer, Guide, Header, VideoPlayer
} from '~/Components';
import DevPanel from '~/Components/DevPanel/DevPanel';
import LoadError from '~/Components/LoadError/LoadError';
import ReplaySidebar from '~/Components/ReplaySidebar/ReplaySidebar';
import StartBooster from '~/Components/StartBooster/StartBooster';
import TopCounter from '~/Components/TopCounter/TopCounter';
import Wallet from '~/Components/Wallet/Wallet';
import BlueprintViewer, { BlueprintViewerRef } from '~/Components/BlueprintViewer/BlueprintViewer';
import DownloadGamePopup from '~/Components/DownloadGamePopup/DownloadGamePopup';
import ContainerDialogs from '~/Containers/ContainerDialogs';
import { useAppDispatch, useAppSelector, useMount, useWindowResize } from '~/hooks';

interface AppState {
    isInGame: boolean,
    isBlurred: boolean,
    isFetching: boolean,
    isLoadErrorVisible: boolean,
    isAnimationInProgress: boolean,
    plugOnWeb: string,
    showDevPanel: boolean,
    showResetButton: boolean,
    showDownloadGamePopup: boolean,
}

const stateSelector = (state: RootState): AppState => {
    const isBlured = isBluredSelector(state);
    const isFetching = state.app.isPendingBuyBooster
        || state.app.isPendingGetData
        || state.wowsCommerce.isLoadingStore;

    return {
        isInGame: state.app.isInGame,
        isBlurred: isBlured,
        isFetching,
        isLoadErrorVisible: state.dialogs.isLoadErrorVisible,
        isAnimationInProgress: state.app.isAnimationInProgress,
        plugOnWeb: state.app.plugOnWeb,
        showDevPanel: state.app.showDevPanel,
        showResetButton: state.app.showResetButton,
        showDownloadGamePopup: state.dialogs.isDownloadGamePopupVisible,
    };
};

function setScaleFactor() {
    if (!document.documentElement) {
        return;
    }
    document.documentElement.style.setProperty('--scale-factor-x', `${window.innerWidth / 1920}`);
    document.documentElement.style.setProperty('--scale-factor-y', `${window.innerHeight / 1080}`);
}

const App = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(stateSelector, equal);

    const {
        isInGame,
        isBlurred,
        isFetching,
        isLoadErrorVisible,
        isAnimationInProgress,
        plugOnWeb,
        showDevPanel,
        showResetButton,
        showDownloadGamePopup,
    } = state;

    const renderDevPanel = (IS_DEV_MODE || IS_SUPER_TEST) && (showDevPanel || showResetButton);

    useMount(() => {
        const devicePixelRatio = Math.round((window.devicePixelRatio || 1) * 100) / 100;
        document.documentElement?.style.setProperty('--device-pixel-ratio', String(devicePixelRatio));

        setScaleFactor();

        document?.body?.focus(); // fix for in game browser
        dispatch(AppInit());

        initWowsCommerce();
        dispatch(loadProducts());
    });

    useWindowResize(setScaleFactor);

    const getData = useCallback(() => {
        dispatch(getDataAction());
    }, [dispatch]);

    const renderBackground = () => {
        if (isInGame) {
            return null;
        }
        const style = {
            backgroundImage: `url(${plugOnWeb})`,
        };
        return <div className={styles.background} style={style} />;
    };

    const wrapper = classnames(styles.wrapper, {
        [styles.wrapperBlured]: isBlurred,
    });

    const setBlueprintViewerRef = useCallback((r: BlueprintViewerRef) => {
        if (r) {
            RefManager.setRef(RefManagerKeys.BlueprintViewer, r);
        }
    }, []);

    return (
        <>
            <Processing isFetching={isFetching} />
            {showDownloadGamePopup ? (
                <DownloadGamePopup />
            ) : (
                <>
                    <VideoPlayer />
                    <div className={wrapper}>
                        {renderBackground()}
                        {blueprintConfig.isShowBlueprint && <BlueprintViewer ref={setBlueprintViewerRef} />}
                        <Header />
                        <TopCounter />
                        {!isAnimationInProgress && <StartBooster />}
                        <ReplaySidebar />
                        <Footer />
                    </div>
                    <ContainerDialogs />
                    <Guide />
                    {isLoadErrorVisible && <LoadError getData={getData} />}
                    {renderDevPanel && <DevPanel />}
                    <Wallet />
                </>
            )}
        </>
    );
};

export default memo(App);
